/* ==========================================================================
   THRDcult homepage animations — GSAP + ScrollTrigger
   - Hero content stage-in
   - Manifesto split-text word reveal
   - Section fade-ups
   - Product / collection card stagger
   - Image-with-text parallax + reveal
   - Marquee speed-up on hover
   - Respects prefers-reduced-motion
   ========================================================================== */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const GSAP_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
  const ST_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js';

  function load(src) {
    return new Promise((res, rej) => {
      const s = document.createElement('script');
      s.src = src;
      s.onload = res;
      s.onerror = rej;
      document.head.appendChild(s);
    });
  }

  load(GSAP_URL)
    .then(() => load(ST_URL))
    .then(init)
    .catch((e) => console.warn('[thrdcult-anim] GSAP failed to load', e));

  function init() {
    if (!window.gsap || !window.ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);

    document.documentElement.classList.add('th-anim-ready');

    const main = document.getElementById('MainContent');
    const template = (main && main.dataset.template) || '';
    const isHome = template === 'index' || template === '';
    const isProduct = template.startsWith('product');

    // Product page animations + early exit on other templates
    if (!isHome && !isProduct) return;
    if (isProduct) {
      runProductPageAnimations();
      return;
    }

    function runProductPageAnimations() {
      // Gallery fade-in on first paint
      const gallery = document.querySelector(
        '.product-information__media, [class*="product-information__media"]'
      );
      if (gallery) {
        gsap.fromTo(
          gallery,
          { opacity: 0, y: 24 },
          { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out' }
        );
      }

      // Right-side info: title, price, variants, button — staggered fade-up
      const infoBlocks = document.querySelectorAll(
        '.product-information [class*="product-title"], ' +
          '.product-information [class*="price"]:not([class*="price__regular"]), ' +
          '.product-information .variant-picker, ' +
          '.product-information .quantity, ' +
          '.product-information .product-form-buttons, ' +
          '.product-information [class*="description"], ' +
          '.product-information [class*="accordion"]'
      );
      if (infoBlocks.length) {
        gsap.fromTo(
          infoBlocks,
          { opacity: 0, y: 18 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: 'power2.out',
            stagger: 0.08,
            delay: 0.15,
          }
        );
      }

      // Subtle parallax on product main image during scroll
      const mainImg = document.querySelector(
        '.product-information__media [class*="product-media-container"] img'
      );
      if (mainImg) {
        const wrapper = mainImg.parentElement;
        if (wrapper && getComputedStyle(wrapper).overflow !== 'hidden') {
          wrapper.style.overflow = 'hidden';
        }
        gsap.fromTo(
          mainImg,
          { yPercent: -3, scale: 1.04 },
          {
            yPercent: 3,
            scale: 1.04,
            ease: 'none',
            scrollTrigger: {
              trigger: mainImg,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
            },
          }
        );
      }
    }

    // ---- 1. Hero content stage-in (per-char heading reveal) --------------
    const heroContent = document.querySelector('[class*="th-hero__content"]');
    if (heroContent) {
      const eyebrow = heroContent.querySelector('[class*="eyebrow"]');
      const heading = heroContent.querySelector('h1, [class*="heading"]');
      const sub = heroContent.querySelector('[class*="sub"]');
      const cta = heroContent.querySelector('a[class*="cta"], button[class*="cta"], [class*="cta"]');

      // Split heading into characters wrapped in clip-spans
      let chars = [];
      if (heading) chars = splitByChar(heading);

      // Single timeline, all fromTo (atomic — no race with CSS pre-hide).
      const tl = gsap.timeline({ delay: 0.2, defaults: { ease: 'power3.out' } });

      if (eyebrow) {
        tl.fromTo(eyebrow,
          { y: 18, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.7 }
        );
      }
      if (chars.length) {
        tl.fromTo(chars,
          { yPercent: 110 },
          {
            yPercent: 0,
            duration: 0.9,
            ease: 'expo.out',
            stagger: 0.025,
            onComplete: () => {
              // Clear inline transform so the chars sit at natural position
              chars.forEach((c) => {
                c.style.transform = '';
                c.style.translate = '';
              });
            },
          },
          eyebrow ? '-=0.3' : 0
        );
      } else if (heading) {
        tl.fromTo(heading,
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.9 },
          eyebrow ? '-=0.3' : 0
        );
      }
      if (sub) {
        tl.fromTo(sub,
          { y: 18, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.7, ease: 'power2.out' },
          '-=0.5'
        );
      }
      if (cta && cta !== heading) {
        tl.fromTo(cta,
          { y: 14, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' },
          '-=0.4'
        );
      }
    }

    function splitByChar(el) {
      // Splits text into per-character spans, preserving words for wrapping.
      // Returns the inner spans (use as GSAP targets).
      const wrap = el.querySelector('strong') || el;
      const text = (wrap.textContent || '').trim();
      if (!text) return [];
      wrap.setAttribute('aria-label', text);
      const words = text.split(/\s+/);
      const html = words
        .map((w) => {
          const charSpans = Array.from(w)
            .map(
              (c) =>
                `<span class="th-char" aria-hidden="true"><span class="th-char__inner">${escapeHTML(
                  c
                )}</span></span>`
            )
            .join('');
          return `<span class="th-word-wrap">${charSpans}</span>`;
        })
        .join(' ');
      wrap.innerHTML = html;
      return Array.from(el.querySelectorAll('.th-char__inner'));
    }

    function escapeHTML(s) {
      return s.replace(/[&<>"']/g, (c) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[c]));
    }

    // ---- 2. Manifesto section watermark + parallax -----------------------
    // Heading was removed; just locate the section by its eyebrow text and
    // attach the MANIFESTO watermark with subtle parallax.
    const manifestoSection = findManifestoSection();
    if (manifestoSection) attachManifestoWatermark(manifestoSection);

    function findManifestoSection() {
      const sections = document.querySelectorAll('main > .shopify-section');
      for (const sec of sections) {
        const text = (sec.textContent || '').toLowerCase();
        if (text.includes('thrdcult manifesto')) return sec;
      }
      return null;
    }

    function attachManifestoWatermark(section) {
      section.classList.add('th-manifesto-section');
      if (section.querySelector('.th-manifesto__bg')) return;

      const bg = document.createElement('div');
      bg.className = 'th-manifesto__bg th-manifesto__bg--inline';
      bg.setAttribute('aria-hidden', 'true');
      bg.textContent = 'MANIFESTO';

      // Insert AFTER the last text-block (the manifesto paragraph) so order is
      // eyebrow → paragraph → watermark → button.
      const textBlocks = section.querySelectorAll(
        '[class*="text-block"], .text-block'
      );
      const lastTextBlock = textBlocks[textBlocks.length - 1];

      if (lastTextBlock && lastTextBlock.parentNode) {
        lastTextBlock.parentNode.insertBefore(bg, lastTextBlock.nextSibling);
      } else {
        // Fallback: insert before the button if no text-block found
        const button = section.querySelector('a.button, button.button');
        if (button && button.parentNode) {
          button.parentNode.insertBefore(bg, button);
        } else {
          section.appendChild(bg);
        }
      }

      // Subtle horizontal parallax
      gsap.fromTo(
        bg,
        { x: '-1%' },
        {
          x: '1%',
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    }

    // ---- 3. Section fade-up (skip hero) ----------------------------------
    const sections = document.querySelectorAll('main > .shopify-section');
    sections.forEach((sec, i) => {
      // skip hero (typically index 0) and any section already marked as hero
      if (i === 0) return;
      if (sec.querySelector('[class*="th-hero"]')) return;
      gsap.fromTo(
        sec,
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: 'power2.out',
          scrollTrigger: { trigger: sec, start: 'top 88%' },
        }
      );
    });

    // ---- 4. Product / collection card stagger ----------------------------
    const cardGrids = document.querySelectorAll(
      '.product-grid, [class*="product-grid"], .collection-list, [class*="collection-list"]'
    );
    cardGrids.forEach((grid) => {
      const cards = grid.querySelectorAll(
        '.product-card, [class*="product-card"], .collection-card, [class*="collection-card"]'
      );
      if (!cards.length) return;
      gsap.set(cards, { y: 36, opacity: 0 });
      gsap.to(cards, {
        y: 0,
        opacity: 1,
        duration: 0.7,
        ease: 'power2.out',
        stagger: 0.07,
        scrollTrigger: { trigger: grid, start: 'top 85%' },
      });
    });

    // ---- 5. Drop sections (XXV, III) — strong portrait parallax ----------
    const dropImageLinks = document.querySelectorAll(
      'main a[href*="afterhours"], main a[href*="precision"], main a[href*="xxv"], main a[href*="iii-"]'
    );
    dropImageLinks.forEach((link) => {
      const img = link.querySelector('img');
      if (!img) return;
      // Crop the link container so the image translates within bounds
      link.style.overflow = 'hidden';
      link.style.display = 'block';
      img.style.willChange = 'transform';
      gsap.fromTo(
        img,
        { yPercent: -12, scale: 1.14 },
        {
          yPercent: 12,
          scale: 1.14,
          ease: 'none',
          scrollTrigger: {
            trigger: link,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.6,
          },
        }
      );
    });

    // ---- 5b. Other editorial images — subtle parallax --------------------
    const editorialImages = document.querySelectorAll(
      'main .image-block img, main .image img'
    );
    editorialImages.forEach((img) => {
      if (
        img.closest(
          '[class*="th-hero"], header, footer, [class*="header"], [class*="footer"], [class*="product-card"], [class*="product-grid"], [class*="collection-card"], [class*="cart-drawer"], a[href*="afterhours"], a[href*="precision"], a[href*="xxv"], a[href*="iii-"]'
        )
      ) {
        return;
      }
      const wrapper = img.parentElement;
      if (wrapper && getComputedStyle(wrapper).overflow !== 'hidden') {
        wrapper.style.overflow = 'hidden';
      }
      gsap.fromTo(
        img,
        { yPercent: -6, scale: 1.05 },
        {
          yPercent: 6,
          scale: 1.05,
          ease: 'none',
          scrollTrigger: {
            trigger: img,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    });

    // ---- 6. Marquee — pause/slow on hover --------------------------------
    document.querySelectorAll('.thrdcult-track').forEach((track) => {
      track.addEventListener('mouseenter', () => {
        track.style.animationDuration = '120s';
      });
      track.addEventListener('mouseleave', () => {
        track.style.animationDuration = '38s';
      });
    });

    // ---- 7. Eyebrow line-draw on scroll-in -------------------------------
    const eyebrows = document.querySelectorAll('main p, main h6');
    eyebrows.forEach((p) => {
      const txt = (p.textContent || '').trim();
      if (txt.startsWith('—') || /^DROP /i.test(txt)) {
        gsap.fromTo(
          p,
          { opacity: 0, x: -16 },
          {
            opacity: 1,
            x: 0,
            duration: 0.8,
            ease: 'power2.out',
            scrollTrigger: { trigger: p, start: 'top 90%' },
          }
        );
      }
    });
  }
})();
